import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  PiScrollLight, PiWarningCircleLight, PiCheckCircleLight, PiClockCountdownLight,
  PiSparkleLight, PiArrowClockwiseLight, PiArrowRightLight, PiCalendarLight,
  PiFilesLight, PiHourglassMediumLight, PiWarningLight, PiShieldWarningLight,
  PiUploadSimpleLight, PiTableLight, PiCalendarBlankLight, PiListChecksLight, PiCheckCircleLight as PiCheckFill,
  PiTrashLight,
} from 'react-icons/pi';
import ContractUploadModal from './ContractUploadModal';
import RenewalsCalendar from './RenewalsCalendar';
import DetailFrame from '../shared/Frames/DetailFrame';
import GlassCard from '../shared/brand/GlassCard';
import { KpiTile } from '../shared/brand/Field';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import { getContractsDashboard, extractContract, unmarkContract, getObligationsFeed, ContractsDashboardWithPubsub, ContractRow, ObligationFeedItem } from '../../utils/contractsApi';
import { rerunOutdatedDrafts } from '../../utils/draftIntelligenceApi';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { useAppDispatch, useAppSelector } from '../../store';
import { useContractPubSub } from './useContractPubSub';

type FilterKey = 'all' | 'attention' | 'expiring_soon' | 'expired';

const STATUS_BADGE: Record<string, { color: 'green' | 'yellow' | 'red' | 'gray'; label: string }> = {
  active: { color: 'green', label: 'Active' },
  expiring_soon: { color: 'yellow', label: 'Expiring soon' },
  expired: { color: 'red', label: 'Expired' },
  unknown: { color: 'gray', label: 'No dates' },
};

const RISK_BADGE: Record<string, { color: 'green' | 'yellow' | 'red'; label: string }> = {
  low: { color: 'green', label: 'Low' },
  medium: { color: 'yellow', label: 'Medium' },
  high: { color: 'red', label: 'High' },
};

/**
 * Invisible per-row subscriber: joins one extracting contract's PubSub group and
 * calls `onDone` when it finishes - replacing the old 8-second poll loop. Kept as
 * a component so the hook can run once per pending row without breaking the rules
 * of hooks.
 */
const ContractLiveRefresh: React.FC<{ uuid: string; userId: string | number | null; onDone: () => void }>
  = ({ uuid, userId, onDone }) => {
    useContractPubSub(uuid, true, onDone, userId);
    return null;
  };

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

const CLMDashboard: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((s: any) => s?.user?.userInfo?.UserID);
  const [data, setData] = useState<ContractsDashboardWithPubsub | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [updating, setUpdating] = useState(false);
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [view, setView] = useState<'table' | 'calendar' | 'obligations'>('table');
  const [obligations, setObligations] = useState<ObligationFeedItem[] | null>(null);
  const [obligationsLoading, setObligationsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getContractsDashboard());
    } catch {
      setData({ contracts: [], upcoming_renewals: [], counts: { total: 0, active: 0, expiring_soon: 0, expired: 0, needs_attention: 0 } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Lazy-load the cross-contract obligations feed the first time it's viewed.
  const loadObligations = useCallback(async () => {
    setObligationsLoading(true);
    try {
      setObligations(await getObligationsFeed());
    } catch {
      setObligations([]);
    } finally {
      setObligationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if ((view === 'obligations' || view === 'calendar') && obligations === null && !obligationsLoading) loadObligations();
  }, [view, obligations, obligationsLoading, loadObligations]);

  const rows = useMemo(() => {
    const all = data?.contracts ?? [];
    if (filter === 'attention') return all.filter((c) => c.needs_attention);
    if (filter === 'expiring_soon') return all.filter((c) => c.status === 'expiring_soon');
    if (filter === 'expired') return all.filter((c) => c.status === 'expired');
    return all;
  }, [data, filter]);

  const openContract = (c: ContractRow) => history.push(`/contracts/${c.uuid}`);

  const toggleSelect = (uuid: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(uuid)) n.delete(uuid); else n.add(uuid);
    return n;
  });
  const allVisibleSelected = rows.length > 0 && rows.every((c) => selected.has(c.uuid));
  const toggleSelectAll = () => setSelected((s) => {
    if (rows.every((c) => s.has(c.uuid))) {
      const n = new Set(s); rows.forEach((c) => n.delete(c.uuid)); return n;
    }
    const n = new Set(s); rows.forEach((c) => n.add(c.uuid)); return n;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkReanalyze = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await Promise.allSettled(ids.map((uuid) => extractContract(uuid)));
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3500, message: t('Re-analyzing {{n}} contract(s)…', { n: ids.length }) }));
      clearSelection();
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not re-analyze the selected contracts.') }));
    } finally { setBulkBusy(false); }
  }, [selected, dispatch, t, load]);

  const bulkUntrack = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await Promise.allSettled(ids.map((uuid) => unmarkContract(uuid)));
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Removed {{n}} contract(s) from the list.', { n: ids.length }) }));
      clearSelection();
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not remove the selected contracts.') }));
    } finally { setBulkBusy(false); }
  }, [selected, dispatch, t, load]);

  const handleUpdateOutdated = useCallback(async () => {
    setUpdating(true);
    try {
      const behind = (data?.contracts ?? [])
        .filter((c) => c.document_type === 'draft' && c.intelligence_status === 'behind')
        .map((c) => c.document_uuid);
      const res = await rerunOutdatedDrafts(behind);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 4000, message: t('Updating {{n}} document(s) from their templates…', { n: res.queued }) }));
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not update the outdated documents.') }));
    } finally {
      setUpdating(false);
    }
  }, [data, dispatch, t, load]);

  const handleRetryExtraction = useCallback(async (c: ContractRow) => {
    setRetrying((r) => ({ ...r, [c.uuid]: true }));
    try {
      await extractContract(c.uuid);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Re-running extraction…') }));
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not re-run extraction.') }));
    } finally {
      setRetrying((r) => ({ ...r, [c.uuid]: false }));
    }
  }, [dispatch, t, load]);

  const counts = data?.counts;
  const allContracts = data?.contracts ?? [];
  const outdatedCount = allContracts.filter((c) => c.intelligence_status === 'behind').length;
  // Contracts still being analysed - each gets a live PubSub subscriber (no poll).
  const pendingContracts = useMemo(
    () => allContracts.filter((c) => c.extraction_status === 'pending' || c.extraction_status === 'processing'),
    [allContracts],
  );
  const extractingCount = pendingContracts.length;

  // Fallback poll while anything is still being analysed. PubSub is the fast
  // path (instant flip when the engine emits), but if the socket is unavailable
  // - blocked by a WAF, negotiate failing, etc. - this still refreshes the list
  // to "done" within a few seconds, so a user never has to reload by hand. It
  // only runs while something is pending and stops the moment the count hits 0.
  useEffect(() => {
    if (extractingCount === 0) return;
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
  }, [extractingCount, load]);

  // Risk mix across analysed contracts - the headline "actual intelligence" signal.
  const riskMix = useMemo(() => {
    const m = { high: 0, medium: 0, low: 0 };
    allContracts.forEach((c) => { if (c.risk_level && m[c.risk_level] != null) m[c.risk_level] += 1; });
    return m;
  }, [allContracts]);
  const analyzedCount = riskMix.high + riskMix.medium + riskMix.low;

  const kpis: { key: FilterKey; label: string; value: number; icon: React.ReactNode; tone?: 'default' | 'warning' | 'danger' | 'info' }[] = [
    { key: 'all', label: t('Contracts'), value: counts?.total ?? 0, icon: <PiFilesLight /> },
    { key: 'expiring_soon', label: t('Expiring soon'), value: counts?.expiring_soon ?? 0, icon: <PiHourglassMediumLight />, tone: 'warning' },
    { key: 'expired', label: t('Expired'), value: counts?.expired ?? 0, icon: <PiWarningLight />, tone: 'danger' },
    { key: 'attention', label: t('Needs attention'), value: counts?.needs_attention ?? 0, icon: <PiWarningCircleLight />, tone: 'info' },
  ];

  const viewToggle = (
    <div className="flex items-center gap-0.5 rounded-full border border-hairline p-0.5">
      {([['table', PiTableLight, t('Table')], ['calendar', PiCalendarBlankLight, t('Calendar')], ['obligations', PiListChecksLight, t('Obligations')]] as const).map(([v, Icon, label]) => (
        <button key={v} type="button" onClick={() => setView(v)}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs transition-colors ${view === v ? 'bg-champagne/10 text-fg-strong' : 'text-fg-muted hover:bg-surface-sunken/60'}`}>
          <Icon size={13} />{label}
        </button>
      ))}
    </div>
  );

  const filterPills = (
    <div className="flex items-center gap-1.5">
      {view === 'table' && (['all', 'attention'] as FilterKey[]).map((k) => (
        <button key={k} type="button" onClick={() => setFilter(k)}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${filter === k ? 'border-champagne/50 bg-champagne/10 text-fg-strong' : 'border-hairline text-fg-muted hover:bg-surface-sunken/60'}`}>
          {k === 'all' ? t('All') : `${t('Needs attention')} (${counts?.needs_attention ?? 0})`}
        </button>
      ))}
      {viewToggle}
    </div>
  );

  return (
    <DetailFrame
      breadcrumbs={[{ name: t('Contracts'), href: '/contracts', current: true }]}
      actions={[
        { label: t('Upload contract'), onClick: () => setUploadOpen(true), icon: <PiUploadSimpleLight className="w-4 h-4" />, variant: 'primary' as const },
        ...(outdatedCount > 0 ? [{
          label: updating ? t('Updating…') : t('Update {{n}} outdated', { n: outdatedCount }),
          onClick: handleUpdateOutdated,
          icon: <PiArrowClockwiseLight className="w-4 h-4" />,
          variant: 'outline' as const,
        }] : []),
      ]}
    >
      <ContractUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
      {/* Live extraction updates - one subscriber per still-analysing contract. */}
      {pendingContracts.map((c) => (
        <ContractLiveRefresh key={c.uuid} uuid={c.uuid} userId={userId} onDone={load} />
      ))}
      <div className="details-canvas h-full w-full overflow-auto custom-scrollbar-main">
        <div className="w-full px-5 py-5 space-y-5">
          <div className="flex items-center gap-3">
            <p className="text-sm text-fg-muted">{t('Every contract in the platform, its health, and what needs attention.')}</p>
            {!loading && extractingCount > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-surface-sunken/60 px-2.5 py-0.5 text-[11px] text-fg-muted">
                <PiSparkleLight size={12} className="animate-pulse text-champagne" />
                {t('{{n}} being analysed…', { n: extractingCount })}
              </span>
            )}
          </div>

          {loading ? (
            <Skeleton variant="detail" />
          ) : (
            <>
              {/* KPI strip */}
              <GlassCard padding="none">
                <div className="details-kpis divide-hairline-soft">
                  {kpis.map((k) => (
                    <button key={k.key} type="button" onClick={() => setFilter(k.key)}
                      className={`text-start transition-colors duration-200 hover:bg-surface-sunken/60 focus:bg-surface-sunken/60 focus:outline-none ${filter === k.key ? 'bg-surface-sunken/50' : ''}`}>
                      <KpiTile icon={k.icon} value={k.value} label={k.label} tone={k.tone} />
                    </button>
                  ))}
                </div>
              </GlassCard>

              <div className="details-body gap-6 items-start">
                {/* Contracts table */}
                <div className="min-w-0 space-y-6">
                  <GlassCard title={t('Contracts')} padding="none" actions={filterPills}>
                    {view === 'obligations' ? (
                      <div className="p-2">
                        {obligationsLoading ? (
                          <div className="p-4"><Skeleton variant="detail" /></div>
                        ) : !obligations || obligations.length === 0 ? (
                          <EmptyState density="compact" icon={<PiListChecksLight />}
                            headline={t('No obligations due')}
                            subtext={t('Open obligations with a due date show up here, overdue first.')} />
                        ) : (
                          <ul className="divide-y divide-hairline-soft">
                            {obligations.map((o) => {
                              const overdue = o.days_to_due != null && o.days_to_due < 0;
                              const soon = o.days_to_due != null && o.days_to_due >= 0 && o.days_to_due <= 7;
                              return (
                                <li key={o.uuid}
                                  onClick={() => history.push(`/contracts/${o.contract_uuid}?tab=Obligations`)}
                                  className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-surface-sunken/60">
                                  <PiCheckFill size={16} className="mt-0.5 shrink-0 text-fg-muted/40" />
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm text-fg-body">{o.description}</div>
                                    <div className="truncate text-[11px] text-fg-muted">{o.contract_title}</div>
                                  </div>
                                  <div className="flex shrink-0 items-center gap-2">
                                    <span className="text-[11px] text-fg-muted">{fmtDate(o.due_date)}</span>
                                    <Badge color={overdue ? 'red' : soon ? 'yellow' : 'gray'} className="text-[10px]">
                                      {overdue ? t('Overdue') : o.days_to_due != null ? t('{{n}}d', { n: o.days_to_due }) : '—'}
                                    </Badge>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ) : view === 'calendar' ? (
                      <div className="p-4">
                        <RenewalsCalendar contracts={allContracts} obligations={obligations || []}
                          onOpen={(uuid) => history.push(`/contracts/${uuid}`)}
                          onOpenHref={(href) => history.push(href)} />
                      </div>
                    ) : rows.length === 0 ? (
                      <EmptyState density="compact" icon={<PiScrollLight />}
                        headline={t('No contracts yet')}
                        subtext={t('Mark a draft or file as a contract to start tracking it here.')} />
                    ) : (
                      <>
                      {selected.size > 0 && (
                        <div className="flex items-center justify-between gap-3 border-b border-hairline-soft bg-surface-sunken/40 px-4 py-2">
                          <span className="text-xs text-fg-body">{t('{{n}} selected', { n: selected.size })}</span>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="small" onClick={bulkReanalyze} isLoading={bulkBusy}
                              icon={<PiArrowClockwiseLight className="h-3.5 w-3.5" />}>{t('Re-analyze')}</Button>
                            <Button variant="neutral" size="small" onClick={bulkUntrack} isLoading={bulkBusy}
                              icon={<PiTrashLight className="h-3.5 w-3.5" />}>{t('Remove from list')}</Button>
                            <button type="button" onClick={clearSelection} className="text-[11px] text-fg-muted hover:text-fg-body">{t('Clear')}</button>
                          </div>
                        </div>
                      )}
                      {/* Horizontal scroll on narrow widths - the columns keep their
                          room and the list scrolls sideways instead of wrapping dates
                          onto three lines. */}
                      <div className="overflow-x-auto custom-scrollbar-main">
                      <table className="w-full min-w-[40rem] text-sm">
                        <thead>
                          <tr className="border-b border-hairline-soft text-[11px] uppercase tracking-wide text-fg-muted">
                            <th className="w-9 px-3 py-2.5 text-start font-normal">
                              <input type="checkbox" aria-label={t('Select all')} checked={allVisibleSelected}
                                onChange={toggleSelectAll} className="cursor-pointer accent-champagne" />
                            </th>
                            <th className="px-4 py-2.5 text-start font-normal">{t('Contract')}</th>
                            <th className="px-3 py-2.5 text-start font-normal whitespace-nowrap">{t('Status')}</th>
                            <th className="px-3 py-2.5 text-start font-normal whitespace-nowrap">{t('Expiry')}</th>
                            <th className="px-3 py-2.5 text-start font-normal">{t('Health')}</th>
                            <th className="px-3 py-2.5" />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((c) => {
                            const sb = STATUS_BADGE[c.status] || STATUS_BADGE.unknown;
                            const parties = (c.parties || []).map((p) => p.name).filter(Boolean).join(' · ');
                            return (
                              <tr key={c.uuid} className="cursor-pointer border-b border-hairline-soft/60 last:border-0 hover:bg-surface-sunken/60"
                                onClick={() => openContract(c)}>
                                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                                  <input type="checkbox" aria-label={t('Select contract')} checked={selected.has(c.uuid)}
                                    onChange={() => toggleSelect(c.uuid)} className="cursor-pointer accent-champagne" />
                                </td>
                                <td className="px-4 py-2.5">
                                  <div className="max-w-[22rem] truncate text-fg-strong">{c.title}</div>
                                  {parties && <div className="max-w-[22rem] truncate text-xs text-fg-muted">{parties}</div>}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap">
                                  <Badge color={sb.color} className="whitespace-nowrap text-[10px]">{t(sb.label)}</Badge>
                                  {c.status === 'expiring_soon' && c.days_to_expiry != null && (
                                    <div className="mt-0.5 text-[11px] text-warning-600 dark:text-warning-400">{t('{{n}} days', { n: c.days_to_expiry })}</div>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap text-fg-body">{fmtDate(c.expiry_date)}</td>
                                <td className="px-3 py-2.5">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    {c.risk_level && RISK_BADGE[c.risk_level] && (
                                      <Badge color={RISK_BADGE[c.risk_level].color} className="inline-flex items-center gap-1 text-[10px]">
                                        <PiShieldWarningLight size={11} />{t(RISK_BADGE[c.risk_level].label)}
                                      </Badge>
                                    )}
                                    {c.intelligence_status === 'behind' && (
                                      <Badge color="yellow" className="inline-flex items-center gap-1 text-[10px]"><PiWarningCircleLight size={11} />{t('Template')}</Badge>
                                    )}
                                    {c.intelligence_status === 'aligned' && (
                                      <Badge color="green" className="inline-flex items-center gap-1 text-[10px]"><PiCheckCircleLight size={11} />{t('Aligned')}</Badge>
                                    )}
                                    {c.risk && c.risk.open_findings > 0 && (
                                      <Badge color="red" className="inline-flex items-center gap-1 text-[10px]"><PiWarningCircleLight size={11} />{t('{{n}} findings', { n: c.risk.open_findings })}</Badge>
                                    )}
                                    {c.extraction_status === 'pending' && (
                                      <Badge color="gray" className="inline-flex items-center gap-1 text-[10px]"><PiSparkleLight size={11} />{t('Extracting…')}</Badge>
                                    )}
                                    {c.extraction_status === 'failed' && (
                                      <button type="button" onClick={(e) => { e.stopPropagation(); handleRetryExtraction(c); }}
                                        disabled={!!retrying[c.uuid]}
                                        className="inline-flex items-center gap-1 rounded-full border border-danger-300 bg-danger-50 px-2 py-0.5 text-[10px] font-medium text-danger-700 hover:bg-danger-100 disabled:opacity-60 dark:border-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
                                        <PiArrowClockwiseLight size={11} className={retrying[c.uuid] ? 'animate-spin' : ''} />
                                        {retrying[c.uuid] ? t('Retrying…') : t('Extraction failed — retry')}
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-end">
                                  <PiArrowRightLight className="inline h-4 w-4 text-fg-muted" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                      </>
                    )}
                  </GlassCard>
                </div>

                {/* Insights + upcoming renewals */}
                <div className="details-side min-w-0 space-y-4">
                  {analyzedCount > 0 && (
                    <GlassCard icon={<PiShieldWarningLight />} title={t('Risk overview')}>
                      <div className="space-y-2.5">
                        {(['high', 'medium', 'low'] as const).map((lvl) => {
                          const n = riskMix[lvl];
                          const pct = analyzedCount ? Math.round((n / analyzedCount) * 100) : 0;
                          const bar = lvl === 'high' ? 'bg-danger-500' : lvl === 'medium' ? 'bg-warning-500' : 'bg-success-500';
                          return (
                            <div key={lvl}>
                              <div className="mb-1 flex items-center justify-between text-[11px]">
                                <span className="capitalize text-fg-body">{t(RISK_BADGE[lvl].label)} {t('risk')}</span>
                                <span className="tabular-nums text-fg-muted">{n}</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-surface-sunken">
                                <div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        <p className="pt-1 text-[11px] text-fg-muted">
                          {t('{{a}} of {{b}} contracts analysed by PONS.', { a: analyzedCount, b: counts?.total ?? 0 })}
                        </p>
                      </div>
                    </GlassCard>
                  )}

                  <GlassCard icon={<PiClockCountdownLight />} title={t('Upcoming renewals')}>
                    {(data!.upcoming_renewals || []).length === 0 ? (
                      <p className="py-1 text-sm text-fg-muted">{t('No renewals coming up.')}</p>
                    ) : (
                      <ul className="space-y-2">
                        {data!.upcoming_renewals.map((r) => (
                          <li key={r.contract_uuid}
                            onClick={() => history.push(`/contracts/${r.contract_uuid}`)}
                            className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-hairline-soft bg-surface-sunken/60 px-3 py-2 hover:bg-surface-sunken">
                            <div className="min-w-0">
                              <div className="truncate text-xs font-medium text-fg-body">{r.title}</div>
                              <div className="flex items-center gap-1 text-[11px] text-fg-muted">
                                <PiCalendarLight size={11} />{fmtDate(r.expiry_date)}
                              </div>
                            </div>
                            <Badge color={r.status === 'expired' ? 'red' : 'yellow'} className="shrink-0 text-[10px]">
                              {r.days_to_expiry != null && r.days_to_expiry < 0 ? t('Overdue') : t('{{n}}d', { n: r.days_to_expiry ?? 0 })}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    )}
                  </GlassCard>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DetailFrame>
  );
};

export default CLMDashboard;
